import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Conditions & Display Labels
const CONDITIONS = {
  absentee: "Absentee Owner",
  longOwnership: "Long Ownership",
  highTaxes: "High Taxes",
  distressed: "Distressed Property",
  vacant: "Vacant Property",
  outOfState: "Out-of-State Owner",      // New condition: Out-of-State Owner
  undervalued: "Undervalued Property",    // New condition: Undervalued Property
};

const DEFAULT_CATEGORY = "Standard Property";

const fetchData = async (fileName) => {
  try {
    const response = await fetch(`/${fileName}`);
    if (!response.ok) throw new Error(`Failed to load ${fileName}`);
    const data = await response.json();
    return data.property || [];
  } catch (error) {
    console.error(`Error loading ${fileName}:`, error);
    return [];
  }
};

const getPropertyCategories = (property, taxData) => {
  const propertyType = property?.summary?.propclass || "Unknown";
  const isDistressed = property?.building?.construction?.condition?.toUpperCase() === "POOR";
  const isVacant = propertyType === "Vacant";
  const isAbsentee = property?.summary?.absenteeInd === "ABSENTEE(MAIL AND SITUS NOT =)";
  const highTaxes = taxData?.assessment?.tax?.taxamt > 5000;
  const longOwnership = property?.summary?.yearbuilt < 1970;

  let categories = [];
  if (isDistressed) categories.push(CONDITIONS.distressed);
  if (isAbsentee) categories.push(CONDITIONS.absentee);
  if (highTaxes) categories.push(CONDITIONS.highTaxes);
  if (longOwnership) categories.push(CONDITIONS.longOwnership);
  if (isVacant) categories.push(CONDITIONS.vacant);

  if (
    property?.owner?.state &&
    property?.address?.countrySubd &&
    property.owner.state.trim().toUpperCase() !== property.address.countrySubd.trim().toUpperCase()
  ) {
    categories.push(CONDITIONS.outOfState);
  }
  if (property?.owner?.state && property?.address?.countrySubd) {
    console.log("Owner state:", property.owner.state, " | Property state:", property.address.countrySubd);
  }
  const assessedValue = taxData?.assessment?.assessed?.assdttlvalue;
  const marketValue = taxData?.assessment?.market?.mktttlvalue;
  if (assessedValue && marketValue && marketValue > assessedValue * 1.5) {
    categories.push(CONDITIONS.undervalued);
  }

  return categories.length > 0 ? categories : [DEFAULT_CATEGORY];
};

const getMarkerColor = (categories) => {
  if (categories.includes(CONDITIONS.distressed)) return "yellow";
  if (categories.includes(CONDITIONS.absentee)) return "red";
  if (categories.includes(CONDITIONS.highTaxes)) return "purple";
  if (categories.includes(CONDITIONS.longOwnership)) return "blue";
  if (categories.includes(CONDITIONS.vacant)) return "black";
  if (categories.includes(CONDITIONS.outOfState)) return "brown";
  if (categories.includes(CONDITIONS.undervalued)) return "gold";
  return "green";
};

// Create Custom Marker Icons
const createIcon = (color) => {
  const colors = {
    red: "marker-icon-red.png",
    blue: "marker-icon-blue.png",
    purple: "marker-icon-violet.png",
    black: "marker-icon-black.png",
    yellow: "marker-icon-yellow.png",
    green: "marker-icon-green.png",
    brown: "marker-icon-brown.png", // New marker icon for out-of-state
    gold: "marker-icon-gold.png",   // New marker icon for undervalued
  };

  return new L.Icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/${colors[color] || "marker-icon-green.png"}`,
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
};

const MapComponent = () => {
  const [properties, setProperties] = useState([]);
  const [taxData, setTaxData] = useState([]);
  const [filters, setFilters] = useState(
    Object.keys(CONDITIONS).reduce((acc, key) => ({ ...acc, [key]: true }), { showAll: true })
  );
  const [showList, setShowList] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      const propertyList = await fetchData("memphis_property_data.json");
      const taxList = await fetchData("memphis_tax_data.json");
      setProperties(propertyList);
      setTaxData(taxList);
    };
    loadData();
  }, []);

  // Handle Filter Toggle
  const toggleFilter = (key) => {
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters, [key]: !prevFilters[key] };
      if (key === "showAll") {
        Object.keys(CONDITIONS).forEach(
          (condition) => (updatedFilters[condition] = updatedFilters[key])
        );
      }
      return updatedFilters;
    });
  };

  // Compute filtered properties (only those with valid coordinates)
  const filteredProperties = properties.filter((property) => {
    const taxInfo = taxData.find((t) => t.identifier.Id === property.identifier.Id);
    const categories = getPropertyCategories(property, taxInfo);
    const isVisible =
      filters.showAll ||
      categories.some((category) =>
        Object.keys(CONDITIONS).some(
          (key) => CONDITIONS[key] === category && filters[key]
        )
      );
    const lat = parseFloat(property.location.latitude);
    const lng = parseFloat(property.location.longitude);
    return isVisible && lat && lng;
  });

  // Updated style for the sliding list panel (wider, centered, limited to 25% of viewport height)
  const listPanelStyle = {
    position: "absolute",
    bottom: "50px",
    left: "50%",
    transform: showList ? "translate(-50%, 0)" : "translate(-50%, 120%)",
    transition: "transform 0.3s ease",
    background: "rgba(255, 255, 255, 0.95)",
    borderRadius: "20px",
    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.15)",
    padding: "20px",
    width: "95%",
    maxWidth: "900px",
    maxHeight: "40vh",
    overflowY: "auto",
    zIndex: 1100,
    fontFamily: "'Roboto', sans-serif",
  };

  const listItemStyle = {
    padding: "10px 0",
    borderBottom: "1px solid #eee",
    display: "flex",
    alignItems: "center",
  };

  const dotStyle = (color) => ({
    backgroundColor: color,
    borderRadius: "50%",
    width: "12px",
    height: "12px",
    marginRight: "10px",
    flexShrink: 0,
  });

  const closeButtonStyle = {
    position: "absolute",
    top: "10px",
    right: "15px",
    background: "transparent",
    border: "none",
    fontSize: "1.5rem",
    cursor: "pointer",
  };

  // Floating button to toggle list view
  const floatingButtonStyle = {
    position: "absolute",
    bottom: "50px",
    right: "20px",
    paddingBottom: 5,
    background: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "50px",
    height: "50px",
    fontSize: "1.5rem",
    cursor: "pointer",
    boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
    zIndex: 1200,
  };

  return (
    <div style={{ position: "relative", height: "1000px", width: "100%" }}>
      <MapContainer center={[35.1495, -90.0490]} zoom={12} style={{ height: "100%", width: "100%" }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {properties.map((property) => {
          const taxInfo = taxData.find((t) => t.identifier.Id === property.identifier.Id);
          const categories = getPropertyCategories(property, taxInfo);
          const markerColor = getMarkerColor(categories);
          const latitude = parseFloat(property.location.latitude);
          const longitude = parseFloat(property.location.longitude);
          if (!latitude || !longitude) return null;
          const isVisible =
            filters.showAll ||
            categories.some((category) =>
              Object.keys(CONDITIONS).some(
                (key) => CONDITIONS[key] === category && filters[key]
              )
            );
          if (!isVisible) return null;
          const icon = createIcon(markerColor);
          return (
            <Marker key={property.identifier.Id} position={[latitude, longitude]} icon={icon}>
  <Popup>
    <strong>{property.address.oneLine}</strong>
    <br />
    <b>Year Built:</b> {property.summary.yearbuilt}
    <br />
    <b>Taxes:</b> ${taxInfo?.assessment?.tax?.taxamt || "N/A"}
    <br />
    {categories.includes(CONDITIONS.undervalued) && (
      <>
        <b>Assessed Value:</b> ${taxInfo?.assessment?.assessed?.assdttlvalue || "N/A"}
        <br />
        <b>Market Value:</b> ${taxInfo?.assessment?.market?.mktttlvalue || "N/A"}
        <br />
      </>
    )}
    <b>Qualifications:</b> {categories.join(", ")}
  </Popup>
</Marker>
          );
        })}
      </MapContainer>

      {/* Top right floating filter panel */}
      <div
        style={{
          position: "absolute",
          top: "20px",
          right: "20px",
          background: "rgba(255, 255, 255, 0.8)",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 8px 16px rgba(0, 0, 0, 0.15)",
          backdropFilter: "blur(8px)",
          zIndex: 1000,
          width: "280px",
          fontFamily: "'Roboto', sans-serif",
          color: "#333",
          transition: "transform 0.3s ease, opacity 0.3s ease",
        }}
      >
        <h3 style={{ margin: 0, marginBottom: "12px", fontSize: "1.2rem", fontWeight: 500, textAlign: "center" }}>
          Filter Properties
        </h3>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
          <input
            type="checkbox"
            id="showAll"
            style={{ marginRight: "10px", width: "18px", height: "18px", cursor: "pointer" }}
            checked={filters.showAll}
            onChange={() => toggleFilter("showAll")}
          />
          <label htmlFor="showAll" style={{ fontSize: "0.95rem", cursor: "pointer" }}>
            Show All Properties
          </label>
        </div>
        {Object.keys(CONDITIONS).map((key) => (
          <div key={key} style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
            <input
              type="checkbox"
              id={key}
              style={{ marginRight: "10px", width: "18px", height: "18px", cursor: "pointer" }}
              checked={filters[key]}
              onChange={() => toggleFilter(key)}
              disabled={filters.showAll}
            />
            <label htmlFor={key} style={{ fontSize: "0.95rem", cursor: "pointer" }}>
              {CONDITIONS[key]}
            </label>
          </div>
        ))}
      </div>

      {/* Floating button to toggle list view */}
      <button style={floatingButtonStyle} onClick={() => setShowList(!showList)}>
        {showList ? "×" : "≡"}
      </button>

      {/* Sliding list panel */}
      <div style={listPanelStyle}>
        <button style={closeButtonStyle} onClick={() => setShowList(false)}>
          ×
        </button>
        <h3 style={{ margin: "0 0 15px 0", textAlign: "center" }}>Properties List</h3>
        {filteredProperties.length ? (
          filteredProperties.map((property) => {
            const taxInfo = taxData.find((t) => t.identifier.Id === property.identifier.Id);
            const categories = getPropertyCategories(property, taxInfo);
            const markerColor = getMarkerColor(categories);
            return (
              <div key={property.identifier.Id} style={listItemStyle}>
                <span style={dotStyle(markerColor)}></span>
                <div>
                  <strong>{property.address.oneLine}</strong>
                  <br />
                  <small>Qualifications: {categories.join(", ")}</small>
                </div>
              </div>
            );
          })
        ) : (
          <p style={{ textAlign: "center" }}>No properties match the filters.</p>
        )}
      </div>
    </div>
  );
};

export default MapComponent;
